import React, { useState } from 'react';
import type { Photo } from 'app/types/commonTypes.type';
import { buildResponsiveImageAttrs } from 'app/shared/utils/fedPhotos';
import { TourLightbox } from './TourLightbox';
import * as styles from './mediaCenter.styles';

type ThreeDTourTabProps = {
  tourUrls: Array<string>;
  tourLabels?: Array<string>;
  photos?: Array<Photo>;
  previewPhoto?: Photo;
};

const CubeIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path
      d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TourPreviewCard = ({
  photo,
  previewPhoto,
  onClick,
  label,
}: {
  photo?: Photo;
  previewPhoto?: Photo;
  onClick: () => void;
  label: string;
}) => {
  const displayPhoto = photo || previewPhoto;
  const previewUrl = displayPhoto?.url;
  const imageAttrs = previewUrl ? buildResponsiveImageAttrs(previewUrl, 768, [384, 768, 1152]) : null;

  return (
    <button className={styles.tourButton} onClick={onClick} aria-label={label}>
      <div className={styles.tourPreview}>
        {imageAttrs ? (
          <img
            src={imageAttrs.src}
            srcSet={imageAttrs.srcSet}
            sizes="100vw"
            alt={`${label} preview`}
            className={styles.tourPreviewImageBlurred}
          />
        ) : previewUrl ? (
          <img src={previewUrl} alt={`${label} preview`} className={styles.tourPreviewImageBlurred} />
        ) : null}
        <div className={styles.tourOverlay}>
          <div className={styles.tourIconCircle}>
            <CubeIcon />
          </div>
          <span className={styles.tourLabel}>{label}</span>
        </div>
      </div>
    </button>
  );
};

export const ThreeDTourTab = ({ tourUrls, tourLabels = [], photos = [], previewPhoto }: ThreeDTourTabProps) => {
  const [activeTourIndex, setActiveTourIndex] = useState<number | null>(null);

  const getTourLabel = (index: number) => tourLabels[index] || `3D Tour ${index + 1}`;

  if (tourUrls.length === 0) {
    return (
      <div role="tabpanel" id="tabpanel-3dtour" aria-labelledby="tab-3dtour">
        <p className={styles.emptyState}>No 3D tour available for this listing.</p>
      </div>
    );
  }

  return (
    <div role="tabpanel" id="tabpanel-3dtour" aria-labelledby="tab-3dtour" className={styles.tourContainer}>
      {tourUrls.length === 1 ? (
        <TourPreviewCard
          photo={photos[0]}
          previewPhoto={previewPhoto}
          onClick={() => setActiveTourIndex(0)}
          label={getTourLabel(0)}
        />
      ) : (
        <div className={styles.tourList}>
          {tourUrls.map((_, i) => (
            <TourPreviewCard
              key={i}
              photo={photos[i]}
              previewPhoto={previewPhoto}
              onClick={() => setActiveTourIndex(i)}
              label={getTourLabel(i)}
            />
          ))}
        </div>
      )}

      {activeTourIndex !== null && (
        <TourLightbox
          tourUrl={tourUrls[activeTourIndex]}
          open={true}
          onOpenChange={(open) => {
            if (!open) setActiveTourIndex(null);
          }}
        />
      )}
    </div>
  );
};
